import React from 'react';
import { Eye, Trash2, Edit } from 'lucide-react';
import { formatDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/billingUtils';

const BillingTable = ({
  projects = [],
  loading = false,
  onView = () => {},
  onEdit = () => {},
  onDelete = () => {}
}) => {
  const getStatusBadgeStyle = (statut) => {
    switch (statut) {
      case 'planifie':
        return 'bg-blue-100 text-blue-800';
      case 'en_cours':
        return 'bg-green-100 text-green-800';
      case 'termine':
        return 'bg-gray-100 text-gray-800';
      case 'suspendu':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (statut) => {
    const labels = {
      planifie: 'Planifié',
      en_cours: 'En cours',
      termine: 'Terminé',
      suspendu: 'Suspendu'
    };
    return labels[statut] || statut;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="flex justify-center items-center py-12">
          <div className="text-gray-600">Chargement des données...</div>
        </div>
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <p className="text-gray-600">Aucun projet à afficher</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Projet
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Catégorie
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                Jours
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                Montant HT
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Facturation
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {projects.map((projet) => (
              <tr key={projet.id} className="hover:bg-gray-50 transition">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {projet.nom}
                  </div>
                  {projet.description && (
                    <div className="text-xs text-gray-500 truncate max-w-xs">
                      {projet.description}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {projet.categorie || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeStyle(projet.statut)}`}>
                    {getStatusLabel(projet.statut)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 font-medium">
                  <div className="flex flex-col items-end gap-1">
                    <span>{projet.jours_consommes.toFixed(2)}</span>
                    <span className="text-xs text-gray-500">
                      @ {formatCurrency(projet.taux || 350)}/j
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="text-sm font-bold text-primary-600">
                    {formatCurrency(projet.montant_a_facturer)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium">
                      {formatDate(new Date(projet.date_facturation_previsionnelle))}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(projet.date_facturation_previsionnelle) < new Date() 
                        ? 'En retard'
                        : 'À venir'
                      }
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={() => onView(projet)}
                      className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition"
                      title="Voir les détails"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => onEdit(projet)}
                      className="text-amber-600 hover:text-amber-900 p-1 hover:bg-amber-50 rounded transition"
                      title="Modifier"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => onDelete(projet)}
                      className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition"
                      title="Supprimer"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BillingTable;


